<template>
  <div class="onboarding-dialog">
    <div class="modal-body">
      <div class="header-img-line">
        <div class="header-img-wrapper">
          <img src="/images/onboarding.svg" />
        </div>
      </div>

      <transition name="fade" mode="out-in">
        <div :key="content.heading">
          <h1>{{ $t(content.heading) }}</h1>
          <p>{{ $t(content.subheading) }}</p>
          <div v-if="content.element === 'checklist'">
            <h3>{{ $t('ONBOARD.CHECKLIST.HEADING') }}</h3>
            <div class="checklist-wrapper">
              <OnboardingChecklist
                compact
                :animate-step="newlyFinishedStep"
                :tracking-source="checklistSource"
                @step-click="markOutcomeReported"
              />
            </div>
          </div>
          <div v-else>
            <OnboardingHelpCards />
            <OriginSurvey v-if="partnerType === 'SHOP'" />
          </div>
          <button
            class="btn btn-primary btn-block btn-lg continue-button"
            :disabled="disableNextBtn"
            @click="goToNextStep"
          >
            {{ $t(content.cta) }}
          </button>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mapState } from 'pinia';
import { useOnboardingStore } from 'commons/stores/onboarding';
import OnboardingChecklist from 'src/app/components/onboarding/OnboardingChecklist.vue';
import OnboardingHelpCards from 'src/app/components/onboarding/OnboardingHelpCards.vue';
import confetti from 'commons/confetti/confetti.js';
import analytics from 'commons/tracking/analytics';
import {
  ONBOARDING_SOURCE,
  trackOnboardingStepDoneCtaClick,
  trackOnboardingStepDoneDismiss,
  trackOnboardingStepDoneImpression,
  trackOnboardingWelcomeCtaClick,
  trackOnboardingWelcomeDismiss,
  trackOnboardingWelcomeImpression,
} from 'commons/onboarding/onboardingTracking';
import OriginSurvey from 'src/app/components/survey/OriginSurvey.vue';

// Both tracked dialogs report the same three events under their own names. The completion screen
// is the step done dialog with nothing left open and reports as one.
const TRACKERS = {
  [ONBOARDING_SOURCE.WELCOME_DIALOG]: {
    impression: trackOnboardingWelcomeImpression,
    ctaClick: trackOnboardingWelcomeCtaClick,
    dismiss: trackOnboardingWelcomeDismiss,
  },
  [ONBOARDING_SOURCE.STEP_DONE_DIALOG]: {
    impression: trackOnboardingStepDoneImpression,
    ctaClick: trackOnboardingStepDoneCtaClick,
    dismiss: trackOnboardingStepDoneDismiss,
  },
};

export default {
  name: 'OnboardingDialog',
  components: {
    OnboardingChecklist,
    OnboardingHelpCards,
    OriginSurvey,
  },
  props: {
    modalInstance: Object,
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      newlyFinishedStep: null,
      onboardingComplete: false,
      disableNextBtn: false,
      outcomeReported: false,
      impressionReported: false,
      openStepsAtImpression: 0,
    };
  },
  computed: {
    ...mapGetters({
      partnerType: 'user/partnerType',
    }),
    ...mapState(useOnboardingStore, {
      firstUnfinishedRelevantStep: 'firstUnfinishedRelevantStep',
      unfinishedSteps: 'unfinishedRelevantSteps',
    }),
    isWelcome() {
      return !!this.data.welcome;
    },
    isStepDone() {
      return !!(this.data.oldSteps && this.data.newSteps);
    },
    checklistSource() {
      return this.isWelcome
        ? ONBOARDING_SOURCE.WELCOME_DIALOG
        : ONBOARDING_SOURCE.STEP_DONE_DIALOG;
    },
    trackers() {
      if (this.isWelcome) {
        return TRACKERS[ONBOARDING_SOURCE.WELCOME_DIALOG];
      }
      if (this.isStepDone) {
        return TRACKERS[ONBOARDING_SOURCE.STEP_DONE_DIALOG];
      }
      return null;
    },
    content() {
      if (this.data.welcome) {
        return {
          heading: 'WELCOME.HEADING',
          subheading: 'WELCOME.SUBHEADING',
          cta: 'WELCOME.START_ONBOARDING',
          element: 'checklist',
        };
      }
      if (this.onboardingComplete) {
        return {
          heading: 'ONBOARD.COMPLETE.HEADING',
          subheading: 'ONBOARD.COMPLETE.SUBHEADING',
          cta: 'ONBOARD.COMPLETE.CTA',
          element: 'help',
        };
      } else {
        return {
          heading: 'ONBOARD.STEP_DONE.HEADING',
          subheading: 'ONBOARD.STEP_DONE.SUBHEADING',
          cta: 'ONBOARD.STEP_DONE.CTA',
          element: 'checklist',
        };
      }
    },
  },
  watch: {
    // The registration flow opens the dialog without waiting for the onboarding store, so the
    // steps routinely arrive after it.
    'unfinishedSteps.steps'() {
      this.reportImpression();
    },
  },
  created() {
    if (this.data.oldSteps && this.data.newSteps) {
      const step = this.data.newSteps.find(
        (newStep) =>
          !this.data.oldSteps.some((oldStep) => oldStep.key === newStep.key),
      );
      this.newlyFinishedStep = step ? step.key : null;
    }

    if (this.trackers) {
      this.interceptDismiss();
      this.reportImpression();
    }

    if (this.unfinishedSteps.steps && !this.unfinishedSteps.steps.length) {
      analytics.events.onboardingComplete();

      this.disableNextBtn = true;
      setTimeout(() => {
        this.onboardingComplete = true;
        confetti.randomCanons();
        this.disableNextBtn = false;
      }, 1500);
    }
  },
  methods: {
    // No count means no impression yet — a reported 0 would be indistinguishable from a real one.
    reportImpression() {
      if (
        !this.trackers ||
        this.impressionReported ||
        !this.unfinishedSteps.steps
      ) {
        return;
      }

      this.impressionReported = true;
      this.openStepsAtImpression = this.unfinishedSteps.steps.length;
      this.trackers.impression({
        partnerType: this.partnerType,
        openSteps: this.openStepsAtImpression,
      });
    },
    // X, Esc and click-outside all pass a gesture; a navigation tearing the dialog down does not.
    interceptDismiss() {
      const dismiss = this.modalInstance.dismiss;

      // eslint-disable-next-line vue/no-mutating-props -- the hook every closing gesture reaches for
      this.modalInstance.dismiss = (payload, gesture) => {
        if (gesture && this.impressionReported && !this.outcomeReported) {
          this.markOutcomeReported();
          this.trackers.dismiss({
            via: gesture,
            openSteps: this.openStepsAtImpression,
          });
        }

        return dismiss(payload, gesture);
      };
    },
    // One outcome per dialog: CTA, checklist entry or dismiss, whichever comes first.
    markOutcomeReported() {
      this.outcomeReported = true;
    },
    goToNextStep() {
      if (this.trackers && this.impressionReported) {
        this.trackers.ctaClick({
          nextStep: this.firstUnfinishedRelevantStep?.key,
        });
      }
      this.markOutcomeReported();

      if (this.firstUnfinishedRelevantStep) {
        this.firstUnfinishedRelevantStep.onClick();
        this.modalInstance.close();
      } else {
        this.modalInstance.close();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/constants';

.onboarding-dialog {
  width: 600px;

  .modal-body {
    padding-top: 24px;
  }

  .header-img-line {
    display: flex;
    justify-content: center;
    width: 100%;
    position: absolute;
    top: -72px;
    left: 0;

    .header-img-wrapper {
      width: 144px;
      height: 144px;
      border-radius: 100%;
      background-color: $white;
      border: 12px solid $white;
      background-color: $grey5;
      display: flex;
      justify-content: center;
      align-items: center;

      img {
        width: 96px;
      }
    }
  }

  h1 {
    margin: 0 0 16px 0;
  }

  p {
    color: $grey60;
    margin: 0 0 24px 0;
  }

  h3 {
    margin: 0 0 8px;
  }

  .checklist-wrapper {
    background-color: $grey5;
    border-radius: $border-radius-medium;
    padding: 8px;
    margin-bottom: 24px;
  }

  .continue-button {
    padding: 12px;
  }
}
</style>

<style>
.dialog-onboarding {
  .dialog-backdrop {
    padding-top: 80px;
  }
}
</style>
